import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Users, UserPlus, UserMinus, DollarSign, Repeat } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api, apiError, money } from "@/lib/api";

export default function AdminMRR() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const [range, setRange] = useState({ from: "", to: "" });
  const [custom, setCustom] = useState(false);

  const load = () => {
    const params = custom && range.from && range.to ? { date_from: range.from, date_to: range.to } : { months };
    api.get("/admin/reports/mrr", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [months, custom, range.from, range.to]);

  if (!data) return <div className="text-zinc-500">Carregando MRR...</div>;
  const a = data.atual, rev = data.receitas;
  const up = a.crescimento_mrr_pct != null && a.crescimento_mrr_pct >= 0;

  return (
    <div className="space-y-5" data-testid="admin-mrr">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-zinc-400">Período:</span>
        {[3, 6, 12].map((m) => (
          <button key={m} data-testid={`mrr-months-${m}`} onClick={() => { setCustom(false); setMonths(m); }}
            className={`px-3 py-1 rounded-sm text-sm border ${!custom && months === m ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-transparent border-white/10 text-zinc-400"}`}>{m} meses</button>
        ))}
        <button data-testid="mrr-custom" onClick={() => setCustom((c) => !c)}
          className={`px-3 py-1 rounded-sm text-sm border ${custom ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-transparent border-white/10 text-zinc-400"}`}>Personalizado</button>
        {custom && (
          <div className="flex items-center gap-2">
            <input data-testid="mrr-from" type="month" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="bg-[#09090B] border border-white/10 rounded-sm h-8 px-2 text-sm text-zinc-200" />
            <span className="text-zinc-500 text-sm">até</span>
            <input data-testid="mrr-to" type="month" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="bg-[#09090B] border border-white/10 rounded-sm h-8 px-2 text-sm text-zinc-200" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ["MRR atual", money(a.mrr), "#22c55e", Repeat, a.crescimento_mrr_pct],
          ["Assinantes ativos", a.assinantes_ativos, "#FFAA00", Users, null],
          ["Novos no mês", a.novos_no_mes, "#FF4D00", UserPlus, null],
          ["Cancelamentos no mês", a.cancelamentos_no_mes, "#ef4444", UserMinus, null],
        ].map(([l, v, c, Ic, gp]) => (
          <div key={l} className="bg-[#121217] border border-white/[0.06] rounded-sm p-4" data-testid={`mrr-kpi-${l}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Ic className="h-3.5 w-3.5" style={{ color: c }} />{l}</div>
            <div className="mono text-2xl font-bold mt-1" style={{ color: c }}>{v}</div>
            {gp != null && <div className={`text-xs mt-1 ${up ? "text-emerald-400" : "text-red-400"}`}>{up ? "▲" : "▼"} {Math.abs(gp)}% vs. mês anterior</div>}
          </div>
        ))}
      </div>

      <div className="bg-[#121217] border border-white/[0.06] rounded-sm p-4" data-testid="mrr-chart">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-400" />Evolução do MRR e assinantes</div>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data.series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
            <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
            <YAxis yAxisId="l" stroke="#71717a" fontSize={11} />
            <YAxis yAxisId="r" orientation="right" stroke="#71717a" fontSize={11} />
            <Tooltip contentStyle={{ background: "#121217", border: "1px solid #ffffff20", borderRadius: 4 }} formatter={(v, n) => (n === "MRR (R$)" ? money(v) : v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="r" dataKey="assinantes_ativos" name="Assinantes ativos" fill="#FFAA0033" radius={[3, 3, 0, 0]} />
            <Line yAxisId="l" type="monotone" dataKey="mrr" name="MRR (R$)" stroke="#22c55e" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-[#121217] border border-white/[0.06] rounded-sm p-4" data-testid="mrr-revenue-chart">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><DollarSign className="h-4 w-4 text-[#FF4D00]" />Receita recorrente x Eventos Avulsos (R$)</div>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data.series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
            <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
            <YAxis stroke="#71717a" fontSize={11} />
            <Tooltip contentStyle={{ background: "#121217", border: "1px solid #ffffff20", borderRadius: 4 }} formatter={(v) => money(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="receita_recorrente" name="Recorrente" fill="#22c55e" radius={[3, 3, 0, 0]} />
            <Bar dataKey="receita_avulso" name="Avulso" fill="#eab308" radius={[3, 3, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[["Receita recorrente (período)", money(rev.recorrente), "text-emerald-400"],
          ["Receita de Eventos Avulsos (período)", money(rev.avulso), "text-amber-400"],
          ["Receita total da plataforma (período)", money(rev.total), "text-accent"]].map(([l, v, c]) => (
          <div key={l} className="bg-[#121217] border border-white/[0.06] rounded-sm p-4" data-testid={`mrr-rev-${l}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
            <div className={`mono text-lg font-bold mt-1 ${c}`}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
